import { Store } from 'vuex';
import { AppState } from '@/store';
import { getPromoBanner } from '@/environment/builders';
import { MUTATIONS as CMS_MUTATIONS } from '@/store/cms';
import { Config } from '@/environment';
import { 
  AdobeTargetResponseIdentityPayload, 
  AdobeTargetResponsePersonalizationPayload, 
  DynamicUpsellContent, 
  HomePageSectionOrder, 
  MealBuilderProteinOrder, 
  PromoBannerContent, 
  TargetDecisionScope, 
  TargetIdentityType, 
  TargetNetworkResponse, 
  TargetPayloadType, 
  TopLevelMenuContent, 
  UtmParameters } from '@/models/adobe-target';
import { AnalyticsService } from '../analytics/analytics.service';
import { Logger } from '../logger/logger';
import { ExperienceRequestEvent, PropositionDisplayEvent } from '../analytics/events';
import { MetadataService } from '../metadata/metadata.service';
import { MUTATIONS } from '@/store/adobe-target';
import { TopLevelMenuGroup, UpsellItem } from '@/models';
import { ConfigService } from '../config/config.service';
import { OrderService } from '../order/order.service';
import intersectionWith from 'lodash/intersectionWith';
import { AnalyticsUpsellType } from '../analytics/platforms/adobe-analytics/models';
import { Dictionary } from 'lodash';
export interface AdobeTargetService {
  processAdobeTargetNetworkResponse(response: TargetNetworkResponse): void;
  setDisplayedUpsellItems(upsellItems: UpsellItem[]): void;
  getPersonalizedUpsellItems(): string[];
  setPromoBannerValues(AEMContentFragment: any, fieldName?: string): void;
  sendStoredPropositionDisplayEvent(scope: TargetDecisionScope): void 
  sendAdobeTargetExperienceRequest(routeName: string | undefined | null, decisionScopes?: TargetDecisionScope[]): void;
  retrieveUtmParameters(query: Dictionary<string | (string | null)[]>): void;
  prehideContent(styleID: PreHideStyleID, timeout?: number): void;
}

export enum PreHideStyleID {
  heroPromoBanner = 'hero-promo-banner-at-style',
  headerNav = 'header-nav-at-style',
  upsellItems = 'upsell-items-at-style',
} 

export class AppAdobeTargetService implements AdobeTargetService {

  constructor(
    private config: Config, 
    private store: Store<AppState>, 
    private logger: Logger, 
    private analytics: AnalyticsService, 
    private metadata: MetadataService, 
    private configService: ConfigService,
    private orderService: OrderService) {}

  processAdobeTargetNetworkResponse(response: TargetNetworkResponse): void {
    // 1. Look for target payload type of 'personalization:decisions'
    // 2. If 'personalization:decisions' exists in a handle,
    //    loop through the payloads and its items to find
    //    the json payload.
    // 3. Get payload decision scope from payload.scope then use
    //    item.data.content for the corresponding decision scope.
    // 4. Store payloads from the decision sopes that we process,
    //    in order to send the pertinent payloads all in the
    //    same PropositionDisplayEvent
    // 5. Repeat the above steps for payload type of 'identity:result'
    const payloadsToSend: AdobeTargetResponsePersonalizationPayload[] = [];
    response.parsedBody?.handle?.filter((handle)=> handle?.type == TargetPayloadType.Personalization)?.forEach(filteredHandle => {
      if (filteredHandle.payload) {
        (filteredHandle.payload as AdobeTargetResponsePersonalizationPayload[]).forEach(payload => {
          payload.items?.forEach(item => {
            switch(payload.scope) {
              case TargetDecisionScope.CartUpsellItems:
                this.storePropositionDisplayData(payload);
                this.setPersonalizedUpsellCmgIds(item?.data?.content as DynamicUpsellContent);
                this.showTargetedContent(PreHideStyleID.upsellItems);
                break;
              case TargetDecisionScope.HeroPromoBannerTop:
                payloadsToSend.push(payload);
                this.setPromoBannerValues(item?.data?.content as PromoBannerContent);
                this.showTargetedContent(PreHideStyleID.heroPromoBanner);
                break;
              case TargetDecisionScope.TopLevelMenu:
                payloadsToSend.push(payload);
                this.setTopLevelMenuSortOrder(item?.data?.content as TopLevelMenuContent);
                break;
              case TargetDecisionScope.HomePageSectionOrder:
                payloadsToSend.push(payload);
                this.setPositionForFeaturedSection(item?.data?.content as HomePageSectionOrder);
                break;
              case TargetDecisionScope.MealBuilderProteinOrder:
                payloadsToSend.push(payload);
                this.setMealBuilderProteinOrder(item?.data?.content as MealBuilderProteinOrder);
                break;
              case TargetDecisionScope.OrderConfirmationGuest:
                payloadsToSend.push(payload);
                this.setGuestOrderConfirmationExperience(item?.data?.content as AEMGuestOrderCreateAccountFragment);
                break;
              case TargetDecisionScope.Global:
                payloadsToSend.push(payload);
                this.showTargetedContent(PreHideStyleID.headerNav);
                break;
              default:
                // Do nothing a decision scope does not match
            }
          });
        });
      }
    });

    response.parsedBody?.handle?.filter((handle)=> handle?.type == TargetPayloadType.Identity)?.forEach(filteredHandle => {
      if (filteredHandle.payload) {
        (filteredHandle.payload as AdobeTargetResponseIdentityPayload[]).forEach(payload => {
          switch(payload.namespace.code) {
            case TargetIdentityType.ECID:
              this.setCustomerECID(payload.id);
              break;
            default:
            // Do nothing for an identity we aren't concerned about
          }
        });
      }
    });

    if (payloadsToSend.length) {
      this.sendPropositionDisplayEvent(payloadsToSend);
    }
  }

  public setDisplayedUpsellItems(upsellItems: UpsellItem[]): void {
    this.store.commit(MUTATIONS.SET_DISPLAYED_UPSELL_ITEMS, upsellItems);
  }

  public getPersonalizedUpsellItems(): string[] {
    const personalizedUpsellCmgIds = this.store.state.adobeTarget.personalizedUpsellCmgIds || [];
    if (!personalizedUpsellCmgIds.length) {
      return [];
    }

    const menuItems = this.store.state.menu.menuItemLookupMap;
    if (!menuItems) {
      return [];
    }

    const aemDefaultUpsellItems = this.configService.getDynamicUpsellItems(this.store.state.order.selectedDeliveryAddress ? 'pickup' : 'delivery')

    // Get intersection of target items and availabe default items
    return intersectionWith(personalizedUpsellCmgIds, aemDefaultUpsellItems, (a, b) => {
      const menuItem = menuItems.get(a);
      return a == b.cmgId && !this.isPersonalizedItemInOrder(a) && !!menuItem?.isItemAvailable && !!menuItem?.unitPrice;
    });
  }

  private setPersonalizedUpsellCmgIds(content: DynamicUpsellContent): void { 
    this.store.commit(MUTATIONS.SET_PERSONALIZED_UPSELL_CMG_IDS, content?.upsellItems || []);
  }

  // Checks to see if personalized upsell item is already in a meal
  // that was added to the order.
  private isPersonalizedItemInOrder(cmgId: string): boolean {
    return this.orderService.getCurrentOrder()?.order.meals?.some(meal => {
      return meal.entrees?.some(e => e.menuItemId == cmgId) || 
             meal.drinks?.some(d => d.menuItemId == cmgId) || 
             meal.sides?.some(s => s.menuItemId == cmgId);
    }) || false;
  }

  private setCustomerECID(ecid: string): void {
    this.store.commit(MUTATIONS.SET_CUSTOMER_ECID, ecid);
  }

  private setPositionForFeaturedSection(homePageSectionOrder: HomePageSectionOrder): void {
    this.store.commit(MUTATIONS.SET_FEATURED_SECTION_ABOVE_TOP_LEVEL_MENU, homePageSectionOrder?.isOneTapSectionAboveTopLevelMenu)
  }

  private async setTopLevelMenuSortOrder(topLevelMenuContent?: TopLevelMenuContent): Promise<void> {
    if (topLevelMenuContent) {
      const metadataResult = await this.metadata.getMenuMetadata();
      const metadata = metadataResult.metadata;
      const groupToPrioritize = metadata.groups.find(g => g.displayName == topLevelMenuContent.firstItem);
      const topLevelMenuGroups: TopLevelMenuGroup[] = JSON.parse(JSON.stringify(metadata.topLevelMenu?.groups));
      const groupIndex = metadata.topLevelMenu?.groups.findIndex(m => m.menuItemGroupId == groupToPrioritize?.id);

      if (groupIndex && groupIndex > -1) {
        topLevelMenuGroups[groupIndex].sortOrder = 0;

        for(let i = 0; i < groupIndex; i++) {
          topLevelMenuGroups[i].sortOrder++;
        }

        this.store.commit(MUTATIONS.SET_TOP_LEVEL_MENU_REORDERING, topLevelMenuGroups);
      }
    } else {
      this.store.commit(MUTATIONS.CLEAR_TOP_LEVEL_MENU_REORDERING);
    }
  }

  private async setMealBuilderProteinOrder(content?: MealBuilderProteinOrder) {
    const variants = [...this.store.state.mealBuilder.displayedVariants];
    const proteinSortOrder = content?.proteinOrder;

    proteinSortOrder?.forEach((pso, i) => {
      if (variants.findIndex(v => v.item.primaryFillingName == pso) == -1) {
        proteinSortOrder.splice(i, 1);
      } 
    });

    proteinSortOrder?.forEach((proteinName, i) => {
      const index = variants.findIndex((v)=> {
        return v.item.primaryFillingName.toLowerCase() == proteinName.toLowerCase();
      });

      if (index > -1) {
        const prioritizedItem = variants.splice(index, 1);
        variants.splice(i, 0, prioritizedItem[0]);
      }
    });

    this.store.commit(MUTATIONS.SET_MEAL_BUILDER_PROTEIN_SORT_ORDER, variants);
  }

  private setGuestOrderConfirmationExperience(orderConfirmationGuestExperience?: AEMGuestOrderCreateAccountFragment) {
    if (orderConfirmationGuestExperience) {
      this.store.commit(MUTATIONS.SET_ORDER_CONFIRMATION_CREATE_ACCOUNT_EXPEREINCE, {
        header: orderConfirmationGuestExperience.master['header-text'],
        description: orderConfirmationGuestExperience.master['subtext-description'],
        medallionImageUrl: `${this.store.state.cms.baseUrl}${orderConfirmationGuestExperience.master['medallion-image']}`
      });
    }
  }

  setPromoBannerValues(promoBannerPayload: any, fieldName?: string) {
    const mutationMap: { [index: string]: keyof typeof CMS_MUTATIONS } = {
      promoBannerTopGuestDesktop: 'SET_PROMO_TOP_GUEST_DESKTOP',
      promoBannerTopAuthenticatedDesktop: 'SET_PROMO_TOP_AUTHENTICATED_DESKTOP',
      promoBannerTopGuestMobile: 'SET_PROMO_TOP_GUEST_MOBILE',
      promoBannerTopAuthenticatedMobile: 'SET_PROMO_TOP_AUTHENTICATED_MOBILE',
      promoBannerTopGuestDesktopCanada: 'SET_PROMO_TOP_GUEST_DESKTOP_CANADA',
      promoBannerTopGuestMobileCanada: 'SET_PROMO_TOP_GUEST_MOBILE_CANADA',
      promoBannerTopAuthenticatedDesktopCanada: 'SET_PROMO_TOP_AUTHENTICATED_DESKTOP_CANADA',
      promoBannerTopAuthenticatedMobileCanada: 'SET_PROMO_TOP_AUTHENTICATED_MOBILE_CANADA',
      promoBannerBottom: 'SET_PROMO_BOTTOM',
      promoBannerBottomCanada: 'SET_PROMO_BOTTOM_CANADA',
    };
    
    // Leaving fieldName as a paramater for backwards compatibility, ie. in case they decide to use an old target activity 
    // with the Target VEC being used.
    let promoBannerType = (fieldName ?? promoBannerPayload?.bannerType) as string;
    switch (promoBannerType) {
      case 'promoBannerTopAuthenticatedDesktop':
      case 'promoBannerTopAuthenticatedMobile':
      case 'promoBannerTopGuestDesktop':
      case 'promoBannerTopGuestMobile':
      case 'promoBannerTopAuthenticatedDesktopCanada':
      case 'promoBannerTopAuthenticatedMobileCanada':
      case 'promoBannerTopGuestDesktopCanada':
      case 'promoBannerTopGuestMobileCanada':
      case 'promoBannerBottom':
      case 'promoBannerBottomCanada':
        const promobanner =  getPromoBanner(this.config.cms.baseUrl, promoBannerPayload);
        this.config.cms[promoBannerType] = promobanner
        this.store.commit(CMS_MUTATIONS[mutationMap[promoBannerType]], this.config.cms[promoBannerType]);
        break;
      default:
        // not every cms item is supported
    }
  }

  sendAdobeTargetExperienceRequest(routeName: string | undefined | null, decisionScopes?: TargetDecisionScope[]): void {
    this.analytics.send(new ExperienceRequestEvent(routeName ?? '', decisionScopes));
  }

  retrieveUtmParameters(query: Dictionary<string | (string | null)[]>): void {
    if (query?.utm_campaign || query?.utm_content || query?.utm_content || query?.utm_medium || query?.utm_source || query?.utm_term || query?.ad_set_id) {  
      const utmParameters: UtmParameters = {
        utmCampaign: query.utm_campaign,
        utmContent: query.utm_content,
        utmMedium: query.utm_medium,
        utmSource: query.utm_source,
        utmTerm: query.utm_term,
        adSetId: query.ad_set_id,
      };

      this.store.commit(MUTATIONS.SET_UTM_PARAMETERS, utmParameters);
    }
  }

  // Use this function to retrieve stored proposition display data for
  // decision scopes that are preloaded. Then remove data from store.
  sendStoredPropositionDisplayEvent(scope: TargetDecisionScope): void {
    const proposition = this.store.state.adobeTarget.propositionDisplayData.get(scope);
    if (proposition) {
      this.sendPropositionDisplayEvent([proposition]);
    }
  }

  // Call this function after each Target view is rendered.
  private sendPropositionDisplayEvent(payloads: AdobeTargetResponsePersonalizationPayload[]): void {
    this.analytics.send(new PropositionDisplayEvent(payloads));
  }

  private storePropositionDisplayData(payload: AdobeTargetResponsePersonalizationPayload): void {
    this.store.commit(MUTATIONS.SET_PROPOSITION_DISPLAY_DATA, payload)
  }

  prehideContent(styleID: PreHideStyleID, timeout: number = 3000): void {
    this.hideTargetedContent(styleID);
    setTimeout(() => {
      this.showTargetedContent(styleID);
    }, timeout);
  }   
   
  private hideTargetedContent(styleID: PreHideStyleID) {
    const parent = document.getElementsByTagName('head')[0];
    if (parent) {
      let sectionSelector;
      if (styleID == PreHideStyleID.heroPromoBanner) {
        sectionSelector = '.promo-banner-container';
      } else if (styleID == PreHideStyleID.headerNav) {
        sectionSelector = '.header-nav';
      } else if (styleID == PreHideStyleID.upsellItems) {
        sectionSelector = '.upsell-container';
      }else {
        return;
      }

      const style = document.createElement('style');
      style.id = styleID;
      style.innerHTML = `${sectionSelector} {opacity: 0 !important}`;
      parent.appendChild(style);
    }
  }

  private showTargetedContent(styleID: PreHideStyleID) {
    const parent = document.getElementsByTagName('head')[0];
    let style = document.getElementById(styleID);
    if (parent && style) {
      parent.removeChild(style);
    }
  }
}
